'use client';

import React from 'react';
import Image from 'next/image';
import type { FilteredCustomerReview, OriginalLanguage } from '@/content/reviews';
import styles from './ReviewCards.module.css';

interface CustomerReviewCardProps {
  review: FilteredCustomerReview;
  currentLocale: 'en' | 'nl' | 'de' | 'sv';
  translations: {
    translatedFrom: string;
    ratingLabel?: string;
    languages: {
      de: string;
      nl: string;
      en: string;
      sv?: string;
    };
  };
}

// Color mapping for profile letters
const getProfileColor = (letter: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788',
    '#E07A5F', '#81B29A', '#F2CC8F', '#6A4C93', '#D4A5A5',
    '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71', '#F39C12',
    '#1ABC9C', '#34495E', '#E67E22', '#95A5A6', '#16A085',
    '#27AE60',
  ];
  const index = letter.toUpperCase().charCodeAt(0) - 65;
  return colors[index % colors.length];
};

const FLAG_ALT_TEXT: Record<string, string> = {
  en: 'English review',
  nl: 'Nederlandse review',
  de: 'Deutsche Bewertung',
};

const Flag: React.FC<{ lang: OriginalLanguage }> = ({ lang }) => {
  const flags = {
    de: '/images/flags/de.svg',
    nl: '/images/flags/nl.svg',
    en: '/images/flags/gb.svg',
  };
  return (
    <span className={styles.flagWrapper}>
      <Image
        src={flags[lang]}
        alt={FLAG_ALT_TEXT[lang] || `${lang} review`}
        width={16}
        height={12}
        className={styles.flag}
      />
    </span>
  );
};

export function CustomerReviewCard({ review, currentLocale, translations }: CustomerReviewCardProps) {
  const reviewText = review.review;
  const showTranslation = review.originalLanguage !== currentLocale;

  return (
    <div className={styles.customerCard}>
      <div className={styles.customerHeader}>
        <div
          className={styles.profileCircle}
          style={{ backgroundColor: getProfileColor(review.profileLetter) }}
        >
          {review.profileLetter}
        </div>
        <div className={styles.customerInfo}>
          <div className={styles.customerName}>{review.name}</div>
          <div className={styles.customerMeta}>{review.date}</div>
        </div>
        <div className={styles.supportRating}>
          <div className={styles.ratingScore}>{review.rating}</div>
          <div className={styles.ratingLabel}>{translations.ratingLabel || 'Rating'}</div>
        </div>
      </div>

      <div className={styles.happyEmoji}>
        <svg width="32" height="32" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="30" fill="#4CAF50" />
          <circle cx="22" cy="26" r="3" fill="white" />
          <circle cx="42" cy="26" r="3" fill="white" />
          <path
            d="M20 40 Q32 48 44 40"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </div>

      <p className={styles.reviewText}>{reviewText}</p>

      {showTranslation && (
        <div className={styles.translationBadge}>
          <Flag lang={review.originalLanguage} />
          <span>
            {translations.translatedFrom} {translations.languages[review.originalLanguage]}
          </span>
        </div>
      )}

      <div className={styles.supportBadge}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
        <span>{review.source}</span>
      </div>
    </div>
  );
}

